import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

import { countText } from "../companyFinance/financeFormat";
import { pct, selectOccupancySplit } from "./performanceSelectors";

/**
 * The selected month's room-days: how many were occupied, how many were not.
 *
 * WHY THE OLD SIX-MONTH CHART IS NOT HERE
 * ---------------------------------------
 * The panel used to carry "Belegung Zimmer · Letzte 6 Monate": six bars, each
 * one a count of ROOMS occupied on the LAST DAY of a month. That is a different
 * axis from everything else on this tab. Performance 2.0 measures ROOM-DAYS
 * across the whole month — a room let for half of March counts as half — so the
 * two answer different questions and can disagree by a lot in any month with a
 * mid-month handover. Carrying the old series over would have put two
 * incompatible occupancy figures on one screen under one heading, and the six
 * bars would have been the one a reader trusts, because a chart looks like more
 * evidence than a number.
 *
 * There is no six-month room-day history to replace it with: the endpoint
 * answers for one month and derives the one before it. Two points are not a
 * trend, so this card shows the two months as two labelled readings and draws no
 * line between them.
 *
 * THE BAR IS PART-TO-WHOLE, WHICH IS THE ONE SHAPE THAT IS HONEST HERE
 * -------------------------------------------------------------------
 * Occupied and vacant room-days are non-negative and sum to the available total
 * the contract publishes, so a stacked bar states a real ratio — the same
 * principle `FinanceAgingCard` already uses. It is `aria-hidden` decoration: the
 * counts and the percentage are printed as text beside it, so a reader who
 * cannot see the bar loses nothing and no figure has to be inferred from a
 * length. With no available room-days there is no denominator and no bar is
 * drawn — a full or an empty one would both be claims.
 */

const CARD = "rounded-xl border border-border bg-card p-4 shadow-sm";

/** One month's reading: percentage, counts, and (optionally) the bar. */
function MonthReading({ heading, split, showBar }) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-[11px] font-medium text-card-foreground">{heading}</span>
        <span className="text-[13px] font-semibold tabular-nums text-card-foreground">
          {pct(split.occupancyPct)}
        </span>
      </div>

      {showBar && split.occupiedShare !== null ? (
        <div
          aria-hidden
          className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full bg-primary"
            style={{ width: `${split.occupiedShare * 100}%` }}
          />
          <div
            className="h-full bg-muted-foreground/25"
            style={{ width: `${(split.vacantShare ?? 0) * 100}%` }}
          />
        </div>
      ) : null}

      <p className="mt-1.5 m-0 text-[11px] tabular-nums text-muted-foreground">
        {countText(split.occupied) ?? "—"} belegte ·{" "}
        {countText(split.vacant) ?? "—"} freie ·{" "}
        {countText(split.available) ?? "—"} verfügbare Raumtage
      </p>
    </div>
  );
}

export default function PerformanceOccupancyCard({
  selected,
  previous,
  stand = "",
  isRange = false,
  loading = false,
}) {
  return (
    <section
      data-testid="performance-occupancy-card"
      aria-labelledby="performance-occupancy-heading"
      aria-busy={loading || undefined}
      className={CARD}
    >
      <h2
        id="performance-occupancy-heading"
        className="m-0 text-[13px] font-semibold text-card-foreground"
      >
        Auslastung nach Raumtagen
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
        {`Belegte Raumtage geteilt durch verfügbare Raumtage${stand}`}
      </p>

      {loading ? (
        <div className="mt-3 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {/* Same rule as the comparison heading below: over a window these are
              period sums, so the month wording goes. Naming the selected column
              "Ausgewählter Monat" beneath a subtitle reading "· April – Juni
              2026" would put one period under another period's name, which is
              the exact failure this slice exists to remove. */}
          <MonthReading
            heading={isRange ? "Ausgewählter Zeitraum" : "Ausgewählter Monat"}
            split={selectOccupancySplit(selected)}
            showBar
          />
          {previous ? (
            <div className="border-t border-border pt-3">
              {/* The previous month is STATED, never subtracted. Two readings
                  side by side; no difference, no direction, no arrow — see
                  PerformanceKpiGrid for why that is a rule and not an omission. */}
              <MonthReading
                heading={isRange ? "Vorperiode" : "Vormonat"}
                split={selectOccupancySplit(previous)}
                showBar
              />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
